import { useEffect, useState } from 'react';
import { CARGO_STATUS_LABEL, formatMoney, type Currency } from 'shared';
import { api } from '../api';
import RouteLine from '../Route';
import AdminList from '../AdminList';
import AdminFilters from '../AdminFilters';
import AdminSheet from '../AdminSheet';
import { alert, confirm, haptic } from '../telegram';

interface Row {
  id: string;
  title: string;
  status: string;
  fromAddress: string;
  toAddress: string;
  priceRub: number;
  price: number;
  currency: Currency;
  blockReason: string | null;
  createdAt: string;
  author: { id: string; login: string | null; firstName: string | null };
  _count: { offers: number };
}

const FILTERS = ['', 'ACTIVE', 'BOOKED', 'BLOCKED', 'DONE'] as const;

/**
 * Модерация заявок.
 *
 * Два разных действия: снять с причиной — заявка остаётся, автор видит,
 * что исправить; удалить без причины — заявка исчезает. Молчаливое
 * удаление выглядит как сбой площадки, поэтому по умолчанию предлагаем
 * первое.
 */
export default function AdminCargos() {
  const [items, setItems] = useState<Row[]>([]);
  const [status, setStatus] = useState<string>('');
  const [busy, setBusy] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  /*
   * Поиск на клиенте: сервер отдаёт двести последних заявок, и просить
   * его о фильтре ради списка такого размера незачем. Когда заявок
   * станет больше — переедет на сервер вместе с постраничной выдачей.
   */
  const [query, setQuery] = useState('');

  async function load() {
    const r = await api.admin.cargos(status || undefined);
    setItems(r.items as unknown as Row[]);
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  async function block(row: Row) {
    const reason = window.prompt(
      `Почему снимаем «${row.title}»? Причину увидит автор.`,
      'Не соответствует правилам площадки',
    );
    if (reason === null) return;
    if (!reason.trim()) {
      await alert('Без причины используйте «Удалить»');
      return;
    }

    setBusy(row.id);
    try {
      await api.deleteCargo(row.id, reason.trim());
      await load();
    } catch (e) {
      await alert((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function remove(row: Row) {
    if (
      !(await confirm(
        `Удалить «${row.title}» без объяснения? Заявка исчезнет, автор получит уведомление.`,
      ))
    ) {
      return;
    }

    setBusy(row.id);
    try {
      await api.deleteCargo(row.id);
      await load();
    } catch (e) {
      await alert((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  const open = items.find((c) => c.id === expanded) ?? null;

  const q = query.trim().toLowerCase();
  const shown = q
    ? items.filter((c) =>
        [c.title, c.fromAddress, c.toAddress, c.author.login ?? '', c.author.firstName ?? '']
          .join(' ')
          .toLowerCase()
          .includes(q),
      )
    : items;

  return (
    <>
      {/* Те же три примитива, что у «Пользователей» и «Компаний»:
          вкладка описывается колонками, а не своей вёрсткой. */}
      <AdminFilters
        active={status === '' ? 0 : 1}
        search={
          <input
            placeholder="Груз, маршрут или автор"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        }
      >
        {FILTERS.map((f) => (
          <button
            key={f || 'all'}
            type="button"
            className={status === f ? 'chip chip--on' : 'chip'}
            onClick={() => {
              haptic();
              setStatus(f);
            }}
          >
            {f ? CARGO_STATUS_LABEL[f] : 'Все'}
          </button>
        ))}
      </AdminFilters>

      <AdminList
        rows={shown}
        keyOf={(c) => c.id}
        empty="Заявок не нашлось"
        // Снятая заявка подсвечена: её ищут в списке, чтобы понять, за что.
        highlight={(c) => c.status === 'BLOCKED'}
        action={{ label: 'Открыть', onClick: (c) => setExpanded(c.id) }}
        columns={[
          {
            title: 'Маршрут',
            cell: (c) => (
              <>
                <RouteLine from={c.fromAddress} to={c.toAddress} />
                <span className="meta" style={{ display: 'block' }}>
                  {c.title}
                </span>
              </>
            ),
          },
          {
            title: 'Ставка',
            width: '150px',
            labelOnPhone: true,
            inline: true,
            cell: (c) => (
              <span className="tabular">{formatMoney(c.price, c.currency)}</span>
            ),
          },
          {
            title: 'Откликов',
            width: '100px',
            labelOnPhone: true,
            inline: true,
            cell: (c) => <span className="tabular">{c._count.offers}</span>,
          },
          {
            title: 'Состояние',
            width: '150px',
            cell: (c) => (
              <span className={c.status === 'BLOCKED' ? 'badge badge--warn' : 'badge'}>
                {CARGO_STATUS_LABEL[c.status] ?? c.status}
              </span>
            ),
          },
          {
            title: 'Автор',
            width: '170px',
            labelOnPhone: true,
            cell: (c) => c.author.login ?? c.author.firstName ?? '—',
          },
          {
            title: 'Создана',
            width: '130px',
            labelOnPhone: true,
            inline: true,
            cell: (c) => (
              // Дата и время, а не «2 дн назад»: жалоба приходит с датой,
              // и сверять её со словами приходится в уме.
              <span className="tabular">
                {new Date(c.createdAt).toLocaleString('ru-RU', {
                  day: '2-digit',
                  month: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            ),
          },
        ]}
      />

      <AdminSheet
        open={open !== null}
        onClose={() => setExpanded(null)}
        title={open?.title ?? ''}
        subtitle={
          open && (
            <>
              {open.fromAddress} → {open.toAddress}
              <div>
                {formatMoney(open.price, open.currency)} · откликов {open._count.offers} ·{' '}
                автор {open.author.login ?? open.author.firstName ?? '—'}
              </div>
            </>
          )
        }
        actions={
          open && (
            <>
              <button className="secondary" onClick={() => setExpanded(null)}>
                Закрыть
              </button>
              {/*
               * Снять можно только заявку, которую ещё не взяли.
               *
               * После брони за ней стоят двое договорившихся: машина едет,
               * а снятие спрятало бы у водителя карточку, контакты и
               * накладные — сделка продолжалась бы вслепую. Сервер такое
               * отклоняет; кнопки здесь нет по той же причине, а не чтобы
               * человек нажал и получил отказ.
               */}
              {open.status === 'ACTIVE' && (
                <>
                  {/* Снятие с причиной идёт первым: заявка остаётся, автор
                      видит, что исправить. Удаление молча выглядит как сбой
                      площадки, поэтому оно вторым и красным. */}
                  <button disabled={busy === open.id} onClick={() => void block(open)}>
                    Снять с причиной
                  </button>
                  <button
                    className="danger"
                    disabled={busy === open.id}
                    onClick={() => void remove(open)}
                  >
                    Удалить
                  </button>
                </>
              )}
            </>
          )
        }
      >
        {open && (
          <>
            <div className="row">
              <span className="meta">Состояние</span>
              <span className={open.status === 'BLOCKED' ? 'badge badge--warn' : 'badge'}>
                {CARGO_STATUS_LABEL[open.status] ?? open.status}
              </span>
            </div>
            <div className="row">
              <span className="meta">Создана</span>
              <span className="tabular">
                {new Date(open.createdAt).toLocaleString('ru-RU')}
              </span>
            </div>
            <div className="row">
              <span className="meta">Ставка в рублях учёта</span>
              <span className="tabular">{formatMoney(open.priceRub, 'RUB')}</span>
            </div>

            {open.blockReason && (
              <div className="error">Снята: {open.blockReason}</div>
            )}

            {/* Объясняем отсутствие кнопок: иначе это выглядит как поломка
                экрана, и следующий шаг человек ищет наугад. */}
            {open.status === 'BOOKED' && (
              <div className="meta" style={{ marginTop: 'var(--s3)' }}>
                Заявка уже в работе — снять её нельзя: за ней стоит сделка
                двух сторон. Если везти этот груз нельзя, рейс отменяется в
                «Рейсах» с указанием причины — обе стороны получают
                уведомление, груз возвращается в ленту, и тогда заявку можно
                снять как обычную.
              </div>
            )}
            {open.status === 'DONE' && (
              <div className="meta" style={{ marginTop: 'var(--s3)' }}>
                Перевозка выполнена — задним числом заявку не снимают. Это
                уже история сделки: на неё опираются отчёты, рейтинг и
                документы обеих сторон.
              </div>
            )}
          </>
        )}
      </AdminSheet>
    </>
  );
}
